import compact from "lodash/compact";
import {
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_PARENT_ACTIVITY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useBookingCategoryLink } from "PFApp/hooks/activity_form/use_booking_category_link";
import { useGetCustomFieldsToLink } from "PFApp/hooks/activity_form/use_get_custom_fields_to_link";
import { usePropertyFieldValueChange } from "PFApp/hooks/activity_form/use_property_field_value_change";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import { Activity } from "PFTypes";
import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useActivityFormStore } from "../../activity_form_store";
import { useEngagementTemplate } from "../use_engagement_template";
import { useFieldsAutoPopulation } from "../use_fields_auto_population";
import { useSubtemplate } from "../use_subtemplate";

type UsePopulateLinkedEngagementFieldsAttributes = {
  engagement?: Activity;
  skipOtherSources?: boolean;
};

export const usePopulateLinkedEngagementFields = () => {
  const template = useActivityFormStore((state) => state.template);
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const subtemplate = useSubtemplate();
  const parentTemplate = useEngagementTemplate();
  const getCustomFieldsToLink = useGetCustomFieldsToLink({ template, parentTemplate });
  const selectedParentActivity = useWatch({ name: FIELD_PARENT_ACTIVITY_KEY });
  const bookingCategoryId = useWatch({ name: FIELD_BOOKING_CATEGORY_ID_KEY });
  const { getBookingCategoryLinkHelpers } = useBookingCategoryLink({ template });

  const { setValue } = useFormContext();

  const changePropertyFieldValue = usePropertyFieldValueChange({
    template,
    subtemplate,
    activityCreator
  });
  const { populateFromAllSources } = useFieldsAutoPopulation();

  return useCallback(
    ({ engagement, skipOtherSources = false }: UsePopulateLinkedEngagementFieldsAttributes) => {
      const parentActivity = engagement || selectedParentActivity;

      if (!parentActivity) {
        return;
      }

      const customFieldsToFill = getCustomFieldsToLink({
        engagement: parentActivity,
        roleSubtemplate: subtemplate
      });

      const { canLink: canLinkBookingCategory, parentBookingCategoryId } = getBookingCategoryLinkHelpers({
        parentActivity,
        activityCreator,
        subtemplate,
        bookingCategoryId
      });

      if (skipOtherSources) {
        customFieldsToFill.forEach((customField) => {
          changePropertyFieldValue(customField.type.name, customField.values);
        });
        canLinkBookingCategory && setValue(FIELD_BOOKING_CATEGORY_ID_KEY, parentBookingCategoryId);
      } else {
        const customFieldsToPopulate = customFieldsToFill.map<FormFieldData>(({ type, values }) => ({
          values,
          fieldName: type.name
        }));
        const engagementDataToPopulate = compact([
          ...customFieldsToPopulate,
          canLinkBookingCategory && {
            fieldName: "booking_category_id",
            values: parentBookingCategoryId!
          }
        ]);
        populateFromAllSources({
          sourceValues: engagementDataToPopulate,
          source: "engagement"
        });
      }
    },
    [
      selectedParentActivity,
      activityCreator,
      bookingCategoryId,
      subtemplate,
      getCustomFieldsToLink,
      getBookingCategoryLinkHelpers,
      setValue,
      changePropertyFieldValue,
      populateFromAllSources
    ]
  );
};
